import React, { PropsWithChildren } from 'react';

import { useCallback, useEffect, useRef } from 'react';
import Auth from '@aws-amplify/auth';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import {
  PushConnectionAcknowledgement,
  PushNotificationEnvelope,
  PushNotificationEvent,
  PushNotificationStreamError,
  PushNotificationType,
} from 'services/types/pushNotifications';
import { NotificationsContext } from 'app/contexts/notifications-context';

const STREAM_PATH = '/push/web';
const MAX_AUTH_RECONNECT_RETRIES = 2;
const AUTH_RECONNECT_DELAY_MS = 500;
const TRANSPORT_RECONNECT_DELAY_MS = 1000;

const getStreamUrl = () => {
  const serviceUrl = process.env.REACT_APP_NOTIFICATION_SERVICE_URL;

  return serviceUrl ? `${serviceUrl}${STREAM_PATH}` : null;
};

const fetchNotificationStream: typeof fetch = (input, init) => {
  const headers = new Headers(init?.headers);
  headers.delete('last-event-id');

  return window.fetch(input, {
    ...init,
    headers,
  });
};

const isAuthError = (error: PushNotificationStreamError) =>
  error.status === 307 || error.status === 401;

const isConnectionAcknowledgement = (
  message: unknown
): message is PushConnectionAcknowledgement => {
  const acknowledgement = message as PushConnectionAcknowledgement;

  return typeof acknowledgement?.userId === 'string' && typeof acknowledgement?.status === 'string';
};

const isSupportedNotification = (message: unknown): message is PushNotificationEnvelope => {
  const notification = message as PushNotificationEnvelope;
  const notificationType = notification?.data?.type;

  return (
    notificationType === PushNotificationType.SUCCESS_NEW_ORDERS ||
    notificationType === PushNotificationType.ORDER_SHIPPED ||
    notificationType === PushNotificationType.REWARDS_BADGE
  );
};

export const NotificationsProvider = ({ children }: PropsWithChildren<{}>) => {
  const controllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const authRetryCountRef = useRef(0);
  const connectRef = useRef<() => void>(() => {});
  const isMountedRef = useRef(true);
  const handlersRef = useRef<Set<(event: PushNotificationEvent) => void>>(new Set());

  const registerHandler = useCallback((handler: (event: PushNotificationEvent) => void) => {
    handlersRef.current.add(handler);

    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current || !isMountedRef.current) return;

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectRef.current();
    }, AUTH_RECONNECT_DELAY_MS);
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    authRetryCountRef.current = 0;

    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    const streamUrl = getStreamUrl();

    if (!streamUrl || controllerRef.current || !isMountedRef.current) return;

    const controller = new AbortController();
    controllerRef.current = controller;

    Auth.currentSession()
      .then((aws) => {
        if (controller.signal.aborted || !isMountedRef.current) return;

        const token = aws['idToken'].jwtToken;

        return fetchEventSource(streamUrl, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          fetch: fetchNotificationStream,
          async onopen(response) {
            if (response.ok && response.status >= 200 && response.status < 300) {
              authRetryCountRef.current = 0;
              return;
            }

            const message = await response.text().catch(() => '');
            const streamError = new Error(
              message || 'Unable to open push notification stream.'
            ) as PushNotificationStreamError;
            streamError.status = response.status;
            streamError.body = message || undefined;
            throw streamError;
          },
          onmessage(event) {
            if (!event?.data) return;

            let message: unknown;

            try {
              message = JSON.parse(event.data);
            } catch (parseError) {
              return;
            }

            if (isConnectionAcknowledgement(message)) return;
            if (isSupportedNotification(message)) {
              const notificationEvent: PushNotificationEvent = {
                event: event.event,
                id: event.id,
                notification: message,
              };

              handlersRef.current.forEach((handler) => handler(notificationEvent));
            }
          },
          onclose() {
            throw new Error('Push notification stream closed.');
          },
          onerror(error) {
            if (isAuthError(error as PushNotificationStreamError)) {
              throw error;
            }

            return TRANSPORT_RECONNECT_DELAY_MS;
          },
        });
      })
      .catch((error: PushNotificationStreamError) => {
        if (controller.signal.aborted || !isMountedRef.current) return;

        controllerRef.current = null;

        if (authRetryCountRef.current >= MAX_AUTH_RECONNECT_RETRIES) return;

        authRetryCountRef.current += 1;
        scheduleReconnect();
      });
  }, [scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    connect();

    return () => {
      isMountedRef.current = false;
      disconnect();
    };
  }, [connect, disconnect]);

  return (
    <NotificationsContext.Provider
      value={{
        registerHandler,
      }}>
      {children}
    </NotificationsContext.Provider>
  );
};
